import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime, formatCurrency, prettify } from "@/lib/format";
import {
  PARTNER_TYPES, PARTNER_PIPELINE, ACTIVE_STATUSES, AGREEMENT_STATUSES, REFERRAL_STATUSES,
  COUNTRIES, QUALITY_RATINGS, TONE, optOf, labelOf, partnerTypeLabel,
} from "@/config/partnerOptions";
import { Pill, QualityStars } from "@/pages/Partners";
import NotesPanel from "@/components/NotesPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Handshake, ChevronLeft, Phone, Mail, Globe, MapPin, Pencil, FileSignature,
  ListChecks, DollarSign, FileText, StickyNote, History, Plus, FolderHeart, Building2,
} from "lucide-react";
import { toast } from "sonner";

const DOT = { partner_created: "#1C4358", referral: "#1C4358", revenue: "#256B4B", document: "#4B5563" };
const TLI = { partner_created: Handshake, referral: FolderHeart, revenue: DollarSign, document: FileText };

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1C4358]/70 mb-0.5">{label}</div>
    <div className="text-sm text-[#252221]">{children || <span className="text-muted-foreground">—</span>}</div>
  </div>
);

const SectionCard = ({ title, icon: Icon, action, children, testid }) => (
  <Card className="p-4 sm:p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="font-display font-semibold text-[#252221] flex items-center gap-2">{Icon && <Icon size={17} className="text-[#1C4358]" />} {title}</h3>
      {action}
    </div>
    {children}
  </Card>
);

const TABS = [
  { value: "overview", label: "Overview", icon: Handshake },
  { value: "referrals", label: "Referrals", icon: ListChecks },
  { value: "revenue", label: "Revenue", icon: DollarSign },
  { value: "documents", label: "Documents", icon: FileText },
  { value: "notes", label: "Notes", icon: StickyNote },
  { value: "timeline", label: "Timeline", icon: History },
];

const PARTNER_FIELDS = ["company_name", "partner_type", "contact_name", "phone", "email", "website",
  "address", "city", "state_province", "country", "service_area", "agreement_status", "referral_terms",
  "insurance_license_notes", "quality_rating", "active_status", "partner_status", "notes"];

export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("partners");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/partners/${id}/full`); setData(d); }
    catch { toast.error("Could not load partner"); navigate("/partners"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const p = data?.partner;

  const savePartner = async (partial, msg = "Partner updated") => {
    try { const { data: u } = await api.put(`/partners/${id}`, partial); setData((prev) => ({ ...prev, partner: { ...prev.partner, ...u } })); if (msg) toast.success(msg); return u; }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); throw err; }
  };

  const openEdit = () => {
    const f = {}; PARTNER_FIELDS.forEach((k) => (f[k] = p[k] ?? ""));
    if (f.quality_rating) f.quality_rating = String(f.quality_rating);
    setEditForm(f); setEditOpen(true);
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    const payload = { ...editForm, quality_rating: editForm.quality_rating ? Number(editForm.quality_rating) : null };
    try { await savePartner(payload, "Partner saved"); setEditOpen(false); } catch { /* handled */ }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-9 w-64" /><Skeleton className="h-24 w-full" /><Skeleton className="h-72 w-full" /></div>;
  if (!p) return null;

  return (
    <div data-testid="partner-detail-page">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
        <Link to="/partners" className="inline-flex items-center gap-1 hover:text-[#1C4358]" data-testid="back-to-partners"><ChevronLeft size={16} /> Partners</Link>
        <span>/</span><span className="text-[#252221] font-medium truncate">{p.display_name}</span>
      </div>

      {/* Header */}
      <Card className="p-4 sm:p-5 border-[#E6EDF2] mb-4" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl font-bold text-[#252221]" data-testid="partner-title">{p.display_name}</h2>
              <Pill arr={PARTNER_PIPELINE} value={p.partner_status} testid="partner-pipeline-badge" />
              <Pill arr={ACTIVE_STATUSES} value={p.active_status} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground">
              {p.partner_type && <span className="inline-flex items-center gap-1"><Building2 size={13} /> {partnerTypeLabel(p.partner_type)}</span>}
              {p.contact_name && <span>{p.contact_name}</span>}
              {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 text-[#1C4358] hover:underline"><Phone size={13} /> {p.phone}</a>}
              {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 text-[#1C4358] hover:underline"><Mail size={13} /> {p.email}</a>}
              {p.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1C4358] hover:underline"><Globe size={13} /> Website</a>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={p.partner_status || ""} onValueChange={(v) => savePartner({ partner_status: v }, "Pipeline updated")} disabled={!canWrite}>
              <SelectTrigger className="h-9 w-[190px]" data-testid="partner-pipeline-select"><SelectValue placeholder="Pipeline" /></SelectTrigger>
              <SelectContent>{PARTNER_PIPELINE.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={p.active_status || ""} onValueChange={(v) => savePartner({ active_status: v }, "Status updated")} disabled={!canWrite}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="partner-active-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTIVE_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            {canWrite && <Button variant="outline" onClick={openEdit} data-testid="edit-partner-button" className="border-[#E6EDF2]"><Pencil size={15} className="mr-1.5" /> Edit</Button>}
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto mmm-scroll -mx-1 px-1">
          <TabsList className="bg-[#F7FAFC] border border-[#E6EDF2] h-auto flex-nowrap w-max">
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`ptab-${t.value}`} className="data-[state=active]:bg-white data-[state=active]:text-[#1C4358] data-[state=active]:shadow-sm whitespace-nowrap">
                <t.icon size={14} className="mr-1.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4 space-y-4" data-testid="poverview-tab">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <SectionCard title="Business details" icon={FileSignature} testid="business-card">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <Field label="Partner type">{p.partner_type ? partnerTypeLabel(p.partner_type) : null}</Field>
                  <Field label="Service area">{p.service_area}</Field>
                  <Field label="Agreement">{p.agreement_status ? <Pill arr={AGREEMENT_STATUSES} value={p.agreement_status} /> : null}</Field>
                  <Field label="Commission / referral terms">{p.referral_terms}</Field>
                  <Field label="Quality rating"><QualityStars value={p.quality_rating} /></Field>
                  <Field label="Location">{[p.city, p.state_province, p.country].filter(Boolean).join(", ")}</Field>
                </div>
                {p.address && <div className="mt-3"><Field label="Address"><span className="inline-flex items-center gap-1"><MapPin size={13} /> {p.address}</span></Field></div>}
                {p.insurance_license_notes && <div className="mt-3"><Field label="Insurance / license notes">{p.insurance_license_notes}</Field></div>}
                {p.notes && <div className="mt-3"><Field label="Notes">{p.notes}</Field></div>}
              </SectionCard>
            </div>
            <div className="space-y-4">
              <SectionCard title="Referral activity" icon={ListChecks} testid="referral-stats-card">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-2xl font-semibold text-[#252221]">{data.stats.total_referrals}</div><div className="text-[11px] text-muted-foreground">Total</div></div>
                  <div><div className="text-2xl font-semibold text-[#1C4358]">{data.stats.active_referrals}</div><div className="text-[11px] text-muted-foreground">Active</div></div>
                  <div><div className="text-2xl font-semibold" style={{ color: "#256B4B" }}>{data.stats.completed_referrals}</div><div className="text-[11px] text-muted-foreground">Completed</div></div>
                </div>
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setTab("referrals")} data-testid="goto-referrals">View referrals</Button>
              </SectionCard>
              <SectionCard title="Revenue generated" icon={DollarSign} testid="revenue-summary-card">
                <div className="text-2xl font-semibold tabular-nums text-[#252221]">{formatCurrency(data.revenue_total || 0)}</div>
                <div className="text-xs text-muted-foreground">{(data.revenue || []).length} record{(data.revenue || []).length === 1 ? "" : "s"}</div>
              </SectionCard>
            </div>
          </div>
        </TabsContent>

        {/* Referrals */}
        <TabsContent value="referrals" className="mt-4" data-testid="preferrals-tab">
          <PartnerReferrals partnerId={id} referrals={data.referrals} canWrite={canWrite} onChange={load} />
        </TabsContent>

        {/* Revenue */}
        <TabsContent value="revenue" className="mt-4" data-testid="prevenue-tab">
          <SectionCard title="Revenue generated" icon={DollarSign}>
            {(!data.revenue || data.revenue.length === 0) ? <p className="text-sm text-muted-foreground py-2">No revenue linked to this partner yet.</p> : (
              <div className="space-y-2">
                {data.revenue.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#E6EDF2] p-3 bg-white" data-testid="partner-revenue-row">
                    <div><div className="font-medium text-[#252221]">{prettify(r.revenue_type) || "Revenue"}</div><div className="text-xs text-muted-foreground">{formatDate(r.created_at)} · {prettify(r.status)}</div></div>
                    <span className="text-sm font-semibold tabular-nums text-[#252221]">{formatCurrency(r.amount || r.expected_amount || 0)}</span>
                  </div>
                ))}
                <div className="flex justify-end pt-2 text-sm font-semibold">Total: <span className="ml-2 tabular-nums">{formatCurrency(data.revenue_total || 0)}</span></div>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="documents" className="mt-4" data-testid="pdocuments-tab">
          <DocumentsPanel relatedType="partners" relatedId={id} branchId={p.branch_id} />
        </TabsContent>
        <TabsContent value="notes" className="mt-4" data-testid="pnotes-tab">
          <NotesPanel relatedType="partners" relatedId={id} branchId={p.branch_id} />
        </TabsContent>
        <TabsContent value="timeline" className="mt-4" data-testid="ptimeline-tab">
          <SectionCard title="Partner timeline" icon={History}>
            {(!data.timeline || data.timeline.length === 0) ? <p className="text-sm text-muted-foreground py-2">No activity yet.</p> : (
              <div className="relative pl-4">
                <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#E6EDF2]" />
                <div className="space-y-3">
                  {data.timeline.map((e, i) => {
                    const Icon = TLI[e.type] || History;
                    return (
                      <div key={i} className="relative flex items-start gap-3" data-testid="partner-timeline-event">
                        <span className="relative z-10 h-3.5 w-3.5 rounded-full border-2 border-white mt-0.5 shrink-0" style={{ backgroundColor: DOT[e.type] || "#9BB9CE" }} />
                        <div className="min-w-0 flex-1 -mt-0.5">
                          <div className="flex items-center gap-1.5 text-sm text-[#252221]"><Icon size={13} className="text-[#1C4358] shrink-0" /><span className="truncate">{e.title}</span></div>
                          <div className="text-[11px] text-muted-foreground">{formatDateTime(e.at)}{e.detail ? ` · ${prettify(e.detail)}` : ""}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto mmm-scroll" data-testid="edit-partner-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Edit partner</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Company name</Label><Input value={editForm.company_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, company_name: e.target.value }))} data-testid="edit-company-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Partner type</Label>
                <Select value={editForm.partner_type || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, partner_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-[280px]">{PARTNER_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Contact person</Label><Input value={editForm.contact_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, contact_name: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Phone</Label><Input value={editForm.phone || ""} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Email</Label><Input value={editForm.email || ""} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Website</Label><Input value={editForm.website || ""} onChange={(e) => setEditForm((f) => ({ ...f, website: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Service area</Label><Input value={editForm.service_area || ""} onChange={(e) => setEditForm((f) => ({ ...f, service_area: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">City</Label><Input value={editForm.city || ""} onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">State / province</Label><Input value={editForm.state_province || ""} onChange={(e) => setEditForm((f) => ({ ...f, state_province: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block text-[13px]">Country</Label>
                <Select value={editForm.country || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, country: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Agreement status</Label>
                <Select value={editForm.agreement_status || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, agreement_status: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{AGREEMENT_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Quality rating</Label>
                <Select value={editForm.quality_rating || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, quality_rating: v }))}>
                  <SelectTrigger data-testid="edit-quality"><SelectValue placeholder="Rate" /></SelectTrigger>
                  <SelectContent>{QUALITY_RATINGS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Commission / referral terms</Label><Input value={editForm.referral_terms || ""} onChange={(e) => setEditForm((f) => ({ ...f, referral_terms: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Insurance / license notes</Label><Textarea rows={2} value={editForm.insurance_license_notes || ""} onChange={(e) => setEditForm((f) => ({ ...f, insurance_license_notes: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={editForm.notes || ""} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-partner-edit">Save partner</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- Partner referrals tab ---- */
const PartnerReferrals = ({ partnerId, referrals, canWrite, onChange }) => {
  const [open, setOpen] = useState(false);
  const [cases, setCases] = useState([]);
  const [form, setForm] = useState({ case_id: "", referral_type: "", status: "sent", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && cases.length === 0) api.get("/cases", { params: { limit: 200 } }).then((r) => setCases(r.data.items || [])).catch(() => {}); }, [open]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/referrals", { partner_id: partnerId, ...form }); toast.success("Referral created"); setOpen(false); setForm({ case_id: "", referral_type: "", status: "sent", notes: "" }); onChange(); }
    catch { toast.error("Could not create referral"); } finally { setBusy(false); }
  };
  const updateStatus = async (rid, status) => {
    try { await api.put(`/referrals/${rid}`, { status }); toast.success("Referral updated"); onChange(); } catch { toast.error("Could not update"); }
  };

  return (
    <SectionCard title="Referrals" icon={ListChecks} testid="partner-referrals-card"
      action={canWrite && <Button size="sm" onClick={() => setOpen(true)} data-testid="new-partner-referral-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={15} className="mr-1" /> New referral</Button>}>
      {(!referrals || referrals.length === 0) ? <p className="text-sm text-muted-foreground py-2">No referrals to this partner yet.</p> : (
        <div className="space-y-2">
          {referrals.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E6EDF2] p-3 bg-white" data-testid="partner-referral-row">
              <div className="min-w-0">
                <div className="font-medium text-[#252221] flex items-center gap-1.5"><FolderHeart size={14} className="text-[#1C4358]" /> {r.case_name || "Unlinked case"}</div>
                <div className="text-xs text-muted-foreground">{r.referral_type ? prettify(r.referral_type) : ""}{r.sent_date ? ` · ${formatDate(r.sent_date)}` : ""}</div>
              </div>
              {canWrite ? (
                <Select value={r.status || ""} onValueChange={(v) => updateStatus(r.id, v)}>
                  <SelectTrigger className="h-8 w-[170px]" data-testid="referral-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REFERRAL_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Pill arr={REFERRAL_STATUSES} value={r.status} />}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="partner-referral-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg">New referral</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><Label className="mb-1.5 block text-[13px]">Family Case</Label>
              <Select value={form.case_id || ""} onValueChange={(v) => setForm((p) => ({ ...p, case_id: v }))}>
                <SelectTrigger data-testid="referral-case-select"><SelectValue placeholder="Choose case" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_name || c.family_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Referral type</Label><Input value={form.referral_type} onChange={(e) => setForm((p) => ({ ...p, referral_type: e.target.value }))} placeholder="e.g. Senior placement" data-testid="referral-type-input" /></div>
            <div><Label className="mb-1.5 block text-[13px]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{REFERRAL_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-partner-referral">{busy ? "Saving..." : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};
